import { spawn, type ChildProcess } from 'node:child_process';
import { randomUUID } from 'node:crypto';
import { existsSync } from 'node:fs';
import path from 'node:path';
import { createInterface, type Interface as ReadlineInterface } from 'node:readline';
import { fileURLToPath } from 'node:url';
import { ZodError } from 'zod';
import { type ParsePythonResult, validateParsePythonResult } from './schemas.js';
import { createLogger } from '../logging/index.js';

const logger = createLogger('python-bridge');

// Re-export for backwards compatibility
export type { ParsePythonResult };

interface PendingRequest {
  resolve: (v: ParsePythonResult) => void;
  reject: (e: Error) => void;
  timeout: NodeJS.Timeout;
}

/**
 * Get the system Python executable name based on platform.
 * Windows uses 'python', Unix-like systems use 'python3'.
 */
function getPythonExecutable(): string {
  return process.platform === 'win32' ? 'python' : 'python3';
}

/**
 * Get the venv Python path based on platform.
 * Windows: .venv/Scripts/python.exe
 * Unix-like: .venv/bin/python3
 */
function getVenvPythonPath(pluginRoot: string): string {
  if (process.platform === 'win32') {
    return path.join(pluginRoot, '.venv', 'Scripts', 'python.exe');
  }
  return path.join(pluginRoot, '.venv', 'bin', 'python3');
}

export class PythonBridge {
  private process: ChildProcess | null = null;
  private readonly pending: Map<string, PendingRequest> = new Map();
  private stoppingIntentionally = false;
  private stdoutReadline: ReadlineInterface | null = null;
  private stderrReadline: ReadlineInterface | null = null;

  start(): Promise<void> {
    if (this.process) return Promise.resolve();

    // Compute absolute path to Python worker using import.meta.url
    // This works both in development (src/) and production (dist/)
    const currentFilePath = fileURLToPath(import.meta.url);
    // Platform-agnostic check: match both /dist/ and \dist\ (Windows)
    const distPattern = `${path.sep}dist${path.sep}`;
    const isProduction = currentFilePath.includes(distPattern);

    let pythonWorkerPath: string;
    let pythonPath: string;

    if (isProduction) {
      // Production: Find dist dir and go to sibling python/ directory
      const distIndex = currentFilePath.indexOf(distPattern);
      const pluginRoot = currentFilePath.substring(0, distIndex);
      pythonWorkerPath = path.join(pluginRoot, 'python', 'ast_worker.py');

      // Use venv python if available (installed by check-dependencies.sh hook)
      const venvPython = getVenvPythonPath(pluginRoot);
      pythonPath = existsSync(venvPython) ? venvPython : getPythonExecutable();
    } else {
      // Development: Go up from src/crawl to find python/
      const srcDir = path.dirname(path.dirname(currentFilePath));
      const projectRoot = path.dirname(srcDir);
      pythonWorkerPath = path.join(projectRoot, 'python', 'ast_worker.py');

      // Development: Use system python (user manages their own environment)
      pythonPath = getPythonExecutable();
    }

    logger.debug(
      { pythonWorkerPath, pythonPath, currentFilePath, isProduction },
      'Starting Python bridge process'
    );

    this.process = spawn(pythonPath, [pythonWorkerPath], {
      stdio: ['pipe', 'pipe', 'pipe'],
      env: {
        ...process.env,
        // Ensure Python uses UTF-8 for stdio on Windows.
        // Without this, Python defaults to the system ANSI code page (e.g. cp1252),
        // causing multi-byte UTF-8 sequences to be misread as surrogates.
        PYTHONUTF8: '1',
        PYTHONIOENCODING: 'utf-8',
      },
    });

    // Add error handler for process spawn errors
    this.process.on('error', (err) => {
      logger.error({ error: err.message, stack: err.stack }, 'Python bridge process error');
      this.rejectAllPending(new Error(`Process error: ${err.message}`));
    });

    // Add exit handler to detect non-zero exits
    this.process.on('exit', (code, signal) => {
      if (code !== 0 && code !== null) {
        logger.error({ code }, 'Python bridge process exited with non-zero code');
        this.rejectAllPending(new Error(`Process exited with code ${String(code)}`));
      } else if (signal && !this.stoppingIntentionally) {
        // Only log if we didn't intentionally stop the process
        logger.error({ signal }, 'Python bridge process killed with signal');
        this.rejectAllPending(new Error(`Process killed with signal ${signal}`));
      }
      this.process = null;
      this.stoppingIntentionally = false;
    });

    // Add stderr logging
    if (this.process.stderr) {
      this.stderrReadline = createInterface({ input: this.process.stderr });
      this.stderrReadline.on('line', (line) => {
        logger.warn({ stderr: line }, 'Python bridge stderr output');
      });
    }

    if (this.process.stdout === null) {
      this.process.kill(); // Kill process to prevent zombie
      this.process = null; // Clean up reference
      return Promise.reject(new Error('Python bridge process stdout is null'));
    }
    this.stdoutReadline = createInterface({ input: this.process.stdout });
    this.stdoutReadline.on('line', (line) => {
      // Filter out non-JSON lines (Python logging output)
      if (!line.trim().startsWith('{')) {
        return;
      }

      try {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        const response = JSON.parse(line) as {
          id: string;
          error?: { message: string };
          result?: unknown;
        };
        const pending = this.pending.get(response.id);
        if (pending !== undefined) {
          if (response.error !== undefined) {
            clearTimeout(pending.timeout);
            this.pending.delete(response.id);
            pending.reject(new Error(response.error.message));
          } else if (response.result !== undefined) {
            clearTimeout(pending.timeout);
            this.pending.delete(response.id);

            // Validate response structure
            try {
              const validated = validateParsePythonResult(response.result);
              pending.resolve(validated);
            } catch (error: unknown) {
              // Log validation failure with original response for debugging
              if (error instanceof ZodError) {
                logger.error(
                  {
                    issues: error.issues,
                    response: JSON.stringify(response.result),
                  },
                  'Python bridge response validation failed'
                );
                pending.reject(
                  new Error(`Invalid response format from Python bridge: ${error.message}`)
                );
              } else {
                const errorMessage = error instanceof Error ? error.message : String(error);
                logger.error({ error: errorMessage }, 'Response validation error');
                pending.reject(new Error(`Response validation error: ${errorMessage}`));
              }
            }
          }
          // If neither result nor error, leave pending (will timeout)
        }
      } catch (err) {
        logger.error(
          {
            error: err instanceof Error ? err.message : String(err),
            line,
          },
          'Failed to parse JSON response from Python bridge'
        );
      }
    });

    return Promise.resolve();
  }

  async parsePython(
    code: string,
    filePath: string,
    timeoutMs: number = 10000
  ): Promise<ParsePythonResult> {
    if (!this.process) await this.start();

    const id = randomUUID();
    const request = {
      jsonrpc: '2.0',
      id,
      method: 'parse_python',
      params: { code, filePath },
    };

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        const pending = this.pending.get(id);
        if (pending) {
          this.pending.delete(id);
          reject(
            new Error(`Python parsing timeout after ${String(timeoutMs)}ms for file: ${filePath}`)
          );
        }
      }, timeoutMs);

      this.pending.set(id, {
        resolve,
        reject,
        timeout,
      });
      if (!this.process?.stdin) {
        reject(new Error('Python bridge process not available'));
        return;
      }
      this.process.stdin.write(
        `${JSON.stringify(request)}\n`,
        (error: Error | null | undefined) => {
          if (error) {
            clearTimeout(timeout);
            this.pending.delete(id);
            reject(new Error(`Failed to send request to Python bridge: ${error.message}`));
          }
        }
      );
    });
  }

  stop(): Promise<void> {
    if (!this.process) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      this.stoppingIntentionally = true;
      this.rejectAllPending(new Error('Python bridge stopped'));

      // Close readline interfaces to prevent resource leaks
      if (this.stdoutReadline) {
        this.stdoutReadline.close();
        this.stdoutReadline = null;
      }
      if (this.stderrReadline) {
        this.stderrReadline.close();
        this.stderrReadline = null;
      }

      // Wait for process to actually exit before resolving
      const proc = this.process;
      if (proc === null) {
        resolve();
        return;
      }

      // Set up exit handler to resolve when process terminates
      const onExit = (): void => {
        resolve();
      };
      proc.once('exit', onExit);

      // Send SIGTERM to gracefully stop
      proc.kill();

      // Safety timeout in case process doesn't exit within 1 second
      setTimeout(() => {
        proc.removeListener('exit', onExit);
        if (this.process === proc) {
          proc.kill('SIGKILL'); // Force kill
          this.process = null;
        }
        resolve();
      }, 1000);
    });
  }

  private rejectAllPending(error: Error): void {
    for (const pending of this.pending.values()) {
      clearTimeout(pending.timeout);
      pending.reject(error);
    }
    this.pending.clear();
  }
}
